<template>
  <div>
    <q-toolbar class="q-pr-none q-mb-md justify-end">
      <q-btn
        outline
        icon="mdi-filter-outline"
        color="primary"
        no-caps
        class="q-px-sm q-mx-sm"
        :label="$t('filter')"
        @click="filter = !filter"
      >
        <q-tooltip> {{ $t('filter_report') }} </q-tooltip></q-btn
      >
      <q-btn
        outline
        color="primary"
        icon-right="archive"
        class="q-mr-sm"
        no-caps
        @click="exportCaptureReport"
        unelevated
      >
        <q-tooltip> {{ $t('capture_export_csv_tooltip') }} </q-tooltip>
      </q-btn>
    </q-toolbar>
    <q-banner dense rounded class="bg-blue-1 text-dark q-mb-sm">
      <template v-slot:avatar>
        <q-icon name="mdi-information-outline" color="primary" />
      </template>
      <div>{{ $t('capture_report_retention_notice') }}</div>
      <div class="q-mt-xs">{{ $t('capture_report_csv_limit_notice') }}</div>
    </q-banner>
    <q-intersection transition="slide-down" v-show="filter">
      <q-card flat v-if="filter" bordered class="q-mb-sm">
        <q-toolbar>
          <q-btn flat round dense class="q-ml-auto" icon="mdi-close" @click="filter = false" />
        </q-toolbar>
        <q-card-section class="q-pt-none row q-col-gutter-md">
          <div class="col-4">
            <q-input outlined v-model="params.orderId" type="text" label="Order ID" />
          </div>
          <div class="col-4">
            <q-input outlined v-model="params.actionId" type="text" label="Action ID" />
          </div>
          <div class="col-4">
            <q-input outlined v-model="params.clickId" type="text" label="Click ID" />
          </div>
          <div class="col-4">
            <q-select
              :label="$t('campaign')"
              outlined
              use-input
              clearable
              input-debounce="300"
              @filter="filterCampaigns"
              v-model="params.campaignId"
              :options="campaignsFiltered"
              option-label="name"
              option-value="id"
              emit-value
              map-options
              @input="onCampaignChange"
            />
          </div>
          <div class="col-4">
            <q-select
              :label="$t('channel_name')"
              outlined
              use-input
              clearable
              input-debounce="300"
              @filter="filterChannels"
              v-model="params.siteId"
              :options="channelsFiltered"
              option-label="name"
              option-value="id"
              emit-value
              map-options
              :loading="loadingChannels"
              :hint="params.campaignId ? $t('capture_channels_filtered_by_campaign') : null"
            />
          </div>
          <div class="col-4">
            <q-select
              :label="$t('capture_status')"
              outlined
              clearable
              multiple
              use-chips
              emit-value
              map-options
              v-model="params.status"
              :options="statusOptions"
            />
          </div>
          <div class="col-4">
            <date-input
              buttons
              outlined
              :label="$t('created_at')"
              :v-model="params.date"
              @update="(value) => (params.date = value)"
              clearable
              range
            />
          </div>
          <div class="col-12">
            <q-checkbox
              v-model="params.includeBad"
              :label="$t('show_bad_transactions')"
              @input="onFilterReports"
            />
            <q-checkbox
              class="q-ml-md"
              v-model="params.onlyClickCampaignIssues"
              :label="$t('only_click_campaign_issues')"
              @input="onFilterReports"
            />
          </div>
          <div class="col-12 text-right">
            <q-btn color="primary" :label="$t('clear')" no-caps outline class="q-px-md q-mx-md" @click="clearFilter" />
            <q-btn color="positive" :label="$t('search')" no-caps unelevated class="q-px-md" @click="onFilterReports" />
          </div>
        </q-card-section>
      </q-card>
    </q-intersection>
  </div>
</template>

<script>
import ReportMixin from '../../mixins/ReportMixin';
import DateInput from '../../widgets/inputsGlobal/DateInput.vue';

export default {
  name: 'HeaderCaptureReports',
  components: { DateInput },
  mixins: [ReportMixin],
  data() {
    const today = this.$moment().format('L');

    return {
      filter: true,
      campaignsList: [],
      campaignsFiltered: [],
      channelsList: [],
      channelsFiltered: [],
      loadingChannels: false,
      params: {
        orderId: null,
        actionId: null,
        clickId: null,
        campaignId: null,
        siteId: null,
        status: [],
        date: `${today} - ${today}`,
        includeBad: false,
        onlyClickCampaignIssues: false
      }
    };
  },

  computed: {
    statusOptions() {
      return [
        { label: this.$t('created'), value: '0' },
        { label: this.$t('approved'), value: '1' },
        { label: this.$t('denied'), value: '2' },
        { label: this.$t('denied_preparation_error'), value: '4' },
        { label: this.$t('denied_duplicate_transaction'), value: '7' },
        { label: this.$t('denied_campaign_not_found'), value: '10' },
        { label: this.$t('denied_awaiting_possible_click'), value: '16' },
        { label: this.$t('blocked_channel_removed'), value: '17' },
        { label: this.$t('denied_cookie_time_expired'), value: '18' },
        { label: this.$t('cap_reached'), value: '19' },
        { label: this.$t('denied_click_too_old'), value: '20' },
        { label: this.$t('denied_click_campaign_mismatch'), value: '21' },
        { label: this.$t('sent_to_raw_transactions'), value: '23' },
        { label: this.$t('received_in_raw_transactions'), value: '24' },
        { label: this.$t('denied_missing_click_id'), value: 'bad' }
      ];
    }
  },

  mounted() {
    this.getCampaigns();
    this.loadChannels();
  },

  methods: {
    async exportCaptureReport() {
      const data = await this.getAllCaptureReport();
      const csvLimit = 5000;
      if (data.length >= csvLimit) {
        this.$q.notify({
          type: 'warning',
          message: this.$t('capture_report_csv_truncated', { limit: csvLimit }),
          timeout: 6000
        });
      }
      this.exportTableCsv(data, this.captureColumns, 'reports');
    },

    async getCampaigns() {
      this.onLoading(true);
      try {
        const { data, status } = await this.$http.get('/redirect?app=ADVERTISER&path=/api/get/campaign');
        if (status == 200) {
          this.campaignsList = data.map((item) => ({
            ...item,
            name: `${item.name} (ID:${item.id})`
          }));
          this.campaignsFiltered = this.campaignsList;
        }
      } catch (error) {
        if (error.response && error.response.status === 500) this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
      } finally {
        this.onLoading(false);
      }
    },

    async loadChannels() {
      this.loadingChannels = true;
      try {
        let list = [];
        if (this.params.campaignId) {
          const { data, status } = await this.$http.get(
            `/redirect?app=ADVERTISER&path=/api/get/campaign/site/approved/list/details/${this.params.campaignId}`
          );
          if (status === 200 && Array.isArray(data)) {
            list = data.map((item) => ({
              id: item.site_id,
              name: `${item.site_name} (ID: ${item.site_id})`
            }));
          }
        } else {
          const { data, status } = await this.$http.get('/redirect?app=AFFILIATE&path=/api/v2/affiliate/site/list');
          if (status === 200 && Array.isArray(data)) {
            list = data.map((item) => ({
              id: item.id,
              name: `${item.name} (ID: ${item.id})`
            }));
          }
        }

        this.channelsList = list;
        this.channelsFiltered = list;

        if (this.params.siteId != null && !list.some((channel) => String(channel.id) === String(this.params.siteId))) {
          this.params.siteId = null;
        }
      } catch (error) {
        if (error.response && error.response.status === 500) this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
        this.channelsList = [];
        this.channelsFiltered = [];
      } finally {
        this.loadingChannels = false;
      }
    },

    onCampaignChange() {
      this.loadChannels();
    },

    filterCampaigns(val, update) {
      update(this.defaultFilter(val, 'campaignsFiltered', this.campaignsList, 'name'));
    },

    filterChannels(val, update) {
      update(this.defaultFilter(val, 'channelsFiltered', this.channelsList, 'name'));
    },

    clearFilter() {
      const today = this.$moment().format('L');

      this.params = {
        orderId: null,
        actionId: null,
        clickId: null,
        campaignId: null,
        siteId: null,
        status: [],
        date: `${today} - ${today}`,
        includeBad: false,
        onlyClickCampaignIssues: false
      };

      this.loadChannels();
      this.ActionSetFilterReports();
      this.getCaptureReport();
    },

    onFilterReports() {
      let filterSelect = [];

      if (this.params.orderId) filterSelect.push(`order_id=${this.params.orderId}`);
      if (this.params.actionId) filterSelect.push(`action_id=${this.params.actionId}`);
      if (this.params.clickId) filterSelect.push(`click_id=${this.params.clickId}`);
      if (this.params.campaignId) filterSelect.push(`campaign_id=${this.params.campaignId}`);
      if (this.params.siteId) filterSelect.push(`site_id=${this.params.siteId}`);
      if (this.params.status && this.params.status.length) {
        const statuses = this.params.status.filter((s) => s !== 'bad');
        const wantsBad = this.params.status.includes('bad');
        if (statuses.length) filterSelect.push(`status=${statuses.join(',')}`);
        if (wantsBad) {
          filterSelect.push('include_bad=1');
          if (statuses.length) filterSelect.push('status_or_bad=1');
          else filterSelect.push('only_bad=1');
        }
      }

      const hasDirectId = !!(this.params.orderId || this.params.actionId || this.params.clickId);
      if (this.params.date && !hasDirectId) {
        let [from, , to] = this.params.date.split(' ');
        from = this.formatToEn(from);
        to = this.formatToEn(to);
        if (from) filterSelect.push(`start_date=${from}`);
        if (to) filterSelect.push(`end_date=${to}`);
      }
      if (this.params.includeBad) filterSelect.push('include_bad=1');
      if (this.params.onlyClickCampaignIssues) filterSelect.push('only_click_campaign_issues=1');
      this.ActionSetFilterReports(filterSelect.join('%26'));
      this.getCaptureReport();
    }
  }
};
</script>
